import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { SectionHeader } from "../../../components/GlobalComponents/SectionHeader/SectionHeader";
import Icon from "../../../components/Icon/Icon";
import { useCustomDashboard } from "./hooks/useCustomDashboards";
import { normalizeContent, SLIDE_MAX_ROWS } from "./utils/dashboardContent";
import { exportSlidesToPdf } from "./utils/exportDashboardPdf";
import DashboardGrid from "./components/DashboardGrid";

export const CustomDashboardViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCustomDashboard(id);
  const [slideIdx, setSlideIdx] = useState(0);
  const [exporting, setExporting] = useState(false);
  const slideRef = useRef(null);

  const content = useMemo(
    () => (data ? normalizeContent(data.content, data.type) : null),
    [data]
  );

  const slides = content?.slides || [];
  const isSlides = data?.type === "slides";
  const current = slides[Math.min(slideIdx, slides.length - 1)] || slides[0];

  const handleExportPdf = async () => {
    if (!slides.length || exporting) return;
    const original = slideIdx;
    setExporting(true);
    try {
      await exportSlidesToPdf({
        slideCount: slides.length,
        showSlide: (i) => setSlideIdx(i),
        getNode: () => slideRef.current,
        fileName: `${data.name || "dashboard"}.pdf`,
      });
    } catch (e) {
      console.error("Export PDF failed", e);
    } finally {
      setSlideIdx(original);
      setExporting(false);
    }
  };

  if (isLoading) return <Box sx={{ p: 3 }}>Cargando…</Box>;
  if (isError || !data)
    return <Box sx={{ p: 3, color: "error.main" }}>No se encontró el dashboard.</Box>;

  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title={data.name}
        subtitle={isSlides ? "Presentación por slides" : "One Page"}
        icon={<Icon name="bold_element_2" size={48} />}
        component={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: "0.75rem" }}
              onClick={() => navigate("/analytics/custom-dashboards")}
            >
              Volver
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem" }}
              onClick={handleExportPdf}
              disabled={exporting || !current || current.items.length === 0}
            >
              {exporting ? "Exportando…" : "Exportar PDF"}
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ fontSize: "0.75rem" }}
              onClick={() => navigate(`/analytics/custom-dashboards/${id}/edit`)}
            >
              Editar
            </Button>
          </Box>
        }
      />

      {isSlides && slides.length > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            my: 2,
          }}
        >
          <IconButton
            size="small"
            disabled={slideIdx === 0}
            onClick={() => setSlideIdx((i) => Math.max(0, i - 1))}
          >
            {/* Same glyph as "next", mirrored — keeps the pair consistent. */}
            <Icon name="bold_arrow_right" size={16} style={{ transform: "rotate(180deg)" }} />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {current?.title || `Slide ${slideIdx + 1}`}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {slideIdx + 1} / {slides.length}
            </Typography>
          </Typography>
          <IconButton
            size="small"
            disabled={slideIdx >= slides.length - 1}
            onClick={() => setSlideIdx((i) => Math.min(slides.length - 1, i + 1))}
          >
            <Icon name="bold_arrow_right" size={16} />
          </IconButton>
        </Box>
      )}

      <Box ref={slideRef} sx={{ mt: 2, bgcolor: "background.default" }}>
        {!current || current.items.length === 0 ? (
          <Box
            className="dyTheme1 dyBorder1 rounded"
            sx={{ p: 5, textAlign: "center", color: "text.secondary" }}
          >
            <Typography variant="body2">Este dashboard todavía no tiene piezas.</Typography>
            <Button
              size="small"
              sx={{ mt: 2 }}
              variant="outlined"
              onClick={() => navigate(`/analytics/custom-dashboards/${id}/edit`)}
            >
              Agregar piezas
            </Button>
          </Box>
        ) : (
          <DashboardGrid
            items={current.items}
            editable={false}
            maxRows={isSlides ? SLIDE_MAX_ROWS : undefined}
          />
        )}
      </Box>
    </Box>
  );
};

export default CustomDashboardViewer;
